/**
 * OpenRouterBalanceCard — owner-admin-only tile that shows the supplier
 * (OpenRouter) credit balance and lets the admin configure low-balance
 * email alerts.
 *
 * Never rendered for end users. Mounted only on /owner-admin/cost-dashboard.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Zap, ExternalLink, RefreshCw, AlertTriangle, CheckCircle2, Mail, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export default function OpenRouterBalanceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState('');
  const [alertEmail, setAlertEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/cost-dashboard/openrouter-balance`, {
        credentials: 'include',
      });
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setThreshold(String(d.threshold_usd ?? ''));
        setAlertEmail(d.alert_email ?? '');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/cost-dashboard/openrouter-balance/alert-config`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          threshold_usd: parseFloat(threshold) || 0,
          alert_email: alertEmail.trim() || null,
        }),
      });
      if (r.ok) {
        toast.success('Alert config updated.');
        await refresh();
      } else {
        toast.error('Could not update.');
      }
    } finally {
      setSaving(false);
    }
  };

  const balance = data?.balance_usd;
  const threshNum = parseFloat(data?.threshold_usd ?? threshold) || 0;
  const isLow = balance != null && balance < threshNum;
  const tone = isLow ? 'red' : balance != null ? 'emerald' : 'zinc';
  const toneClasses = {
    red: 'border-red-500/40 bg-red-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
    zinc: 'border-zinc-500/20 bg-zinc-500/5',
  }[tone];

  return (
    <Card className={toneClasses} data-testid="openrouter-balance-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Zap className="w-4 h-4 text-amber-500" />
          AI Supplier Credit Balance
          <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
            Admin only · Never shown to users
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Balance row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Tile label="Current balance" value={fmtUsd(balance)} highlight={isLow ? 'red' : 'emerald'} testId="openrouter-balance" />
          <Tile label="Lifetime topped up" value={fmtUsd(data?.topped_up_usd)} testId="openrouter-topup" />
          <Tile label="Lifetime usage" value={fmtUsd(data?.usage_usd)} testId="openrouter-usage" />
          <Tile label="Alert threshold" value={fmtUsd(threshNum)} testId="openrouter-threshold" />
        </div>

        {isLow && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-red-500/40 bg-red-500/10 text-red-500 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Balance below your threshold.</strong> Top up now to keep paid models running.
              Users on the free tier will continue working — only premium model spend will be capped.
            </div>
          </div>
        )}

        {balance != null && !isLow && (
          <div className="flex items-center gap-2 text-xs text-emerald-500">
            <CheckCircle2 className="w-3.5 h-3.5" /> Supplier balance is healthy.
          </div>
        )}

        {data?.error && (
          <div className="text-xs text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> {data.error}
          </div>
        )}

        {/* Alert config */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1 block">
              Low-balance threshold (USD)
            </label>
            <Input
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              type="number"
              step="1"
              min="0"
              data-testid="openrouter-threshold-input"
              className="bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1 block flex items-center gap-1">
              <Mail className="w-3 h-3" /> Alert email
            </label>
            <Input
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
              type="email"
              placeholder="eli@countbucks.com"
              data-testid="openrouter-alert-email"
              className="bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={saveConfig}
            disabled={saving}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 gap-1.5"
            data-testid="openrouter-save-config"
          >
            <Save className="w-3.5 h-3.5" /> Save alert config
          </Button>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="gap-1.5"
            data-testid="openrouter-topup-link"
          >
            <a href="https://openrouter.ai/credits" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-3.5 h-3.5" /> Top up at supplier
            </a>
          </Button>
          <Button onClick={refresh} variant="ghost" size="sm" className="gap-1.5" data-testid="openrouter-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {data?.last_alert_sent_at && (
            <span className="text-[11px] text-muted-foreground ml-auto">
              Last alert: {new Date(data.last_alert_sent_at).toLocaleString()}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, highlight, testId }) {
  const tone = highlight === 'red'
    ? 'text-red-400'
    : highlight === 'emerald'
      ? 'text-emerald-400'
      : 'text-white';
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={testId}>
      <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </p>
      <p className={`text-xl font-bold tabular-nums mt-1 ${tone}`}>{value}</p>
    </div>
  );
}
